import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from 'atoms';
import { useQueryParams } from 'hooks';
import { getTemperatureListAction } from 'store/pageSlice';
import { initialPaginateConfig } from 'utils';
import { TemperatureTableConfig } from 'utils/table-config/TempratureTableConfig';
import {
	disconnectSocket,
	initializeSocket,
	useSocket,
	useSocketSubscription,
} from 'utils/websocket';
import { LoadingHistoryModal } from 'components';
import AlertDetail from './AlertDetail';
import AlertSection from './AlertSection';
import FilterSection from './FilterSection';
import TableHeaderSection from './TableHeaderSection';
import InfiniteScrollTable from './InfiniteScrollTable';
import { allOption, durationFilterOptions, alertStatusOptions } from './constants';
import useAuth from 'hooks/useAuth';

export default function TemperatureDashboard() {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [filterConfig, setFilterConfig] = useQueryParams({
		search_type: 'temperature_tracking',
		filter_list: {},
		filter_by: {},
		persistKey: 'temperatureDashboard:filters',
	});
	const [data, setData] = useState([]);
	const [open, setOpen] = useState(false);
	const [cities, setCities] = useState([]);
	const [statuses, setStatuses] = useState([]);
	const [alerts, setAlerts] = useState([]);
	const [selectedAlert, setSelectedAlert] = useState(null);
	const [alertModalVisible, setAlertModalVisible] = useState(false);
	const [alertsLoading, setAlertsLoading] = useState(false);
	const [activeFilters, setActiveFilters] = useState({});
	const [temperatureDataReceived, setTemperatureDataReceived] = useState(false);
	const [alertDataReceived, setAlertDataReceived] = useState(false);
	const [isLoadingData, setIsLoadingData] = useState(false);

	// Loading History Modal state
	const [loadingHistoryModalVisible, setLoadingHistoryModalVisible] = useState(false);
	const [selectedTripForHistory, setSelectedTripForHistory] = useState(null);

	const auth = useAuth();
	const accessToken = auth?.access_token;

	const { socketConnection, isConnected } = useSocket('temperature_data', accessToken);

	const initializeFiltersFromURL = useCallback(() => {
		const formValues = {};

		if (filterConfig.city) formValues.city = filterConfig.city;
		if (filterConfig.min_temperature) formValues.min_temperature = filterConfig.min_temperature;
		if (filterConfig.max_temperature) formValues.max_temperature = filterConfig.max_temperature;
		if (filterConfig.time_filter) formValues.time_filter = filterConfig.time_filter;
		if (filterConfig.status) formValues.status = filterConfig.status;
		if (filterConfig.alert_status) formValues.alert_status = filterConfig.alert_status;

		return formValues;
	}, [filterConfig]);

	const createWebsocketFilters = useCallback((formValues) => {
		const wsFilters = {};
		if (formValues.city) wsFilters.city = formValues.city;
		if (formValues.min_temperature)
			wsFilters.minimum_temperature = parseFloat(formValues.min_temperature);
		if (formValues.max_temperature)
			wsFilters.maximum_temperature = parseFloat(formValues.max_temperature);
		if (formValues.time_filter) wsFilters.duration = formValues.time_filter;
		if (formValues.status) wsFilters.status = formValues.status;
		if (formValues.alert_status) wsFilters.alert_status = formValues.alert_status;

		return wsFilters;
	}, []);

	useEffect(() => {
		const formValues = initializeFiltersFromURL();
		form.setFieldsValue(formValues);

		const wsFilters = createWebsocketFilters(formValues);

		if (Object.keys(wsFilters).length > 0) {
			setActiveFilters(wsFilters);

			if (socketConnection && isConnected) {
				socketConnection.emit('temperature_data', wsFilters);
				socketConnection.emit('alert_data', wsFilters);
			}
		}
	}, [
		filterConfig,
		form,
		socketConnection,
		isConnected,
		initializeFiltersFromURL,
		createWebsocketFilters,
	]);

	useEffect(() => {
		initializeSocket();
		const handleBeforeUnload = () => {
			disconnectSocket();
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			disconnectSocket();
		};
	}, []);

	useEffect(() => {
		if (socketConnection && isConnected && Object.keys(activeFilters).length > 0) {
			socketConnection.emit('temperature_data', activeFilters);
		}
	}, [socketConnection, isConnected, activeFilters]);

	useSocketSubscription(
		'temperature_data',
		'temperature_data',
		(updatedData) => {
			if (updatedData !== undefined) {
				setTemperatureDataReceived(true);
				if (Array.isArray(updatedData)) {
					setData(updatedData);
					const uniqueCities = [...new Set(updatedData.map((item) => item.city))]
						.filter(Boolean)
						.map((city) => ({
							value: city,
							label: city,
						}));
					setCities([allOption, ...uniqueCities]);

					const uniqueStatuses = [...new Set(updatedData.map((item) => item.status))]
						.filter(Boolean)
						.map((status) => ({
							value: status,
							label: status,
						}));
					setStatuses([allOption, ...uniqueStatuses]);
				} else {
					setData([]);
				}
			}
		},
		accessToken
	);

	useEffect(() => {
		if (socketConnection && isConnected && Object.keys(activeFilters).length > 0) {
			socketConnection.emit('alert_data', activeFilters);
		}
	}, [socketConnection, isConnected, activeFilters]);

	useSocketSubscription(
		'alert_data',
		'alert_data',
		(newAlerts) => {
			if (newAlerts !== undefined) {
				setAlertDataReceived(true);
				if (Array.isArray(newAlerts)) {
					setAlerts(newAlerts);
				} else {
					setAlerts([]);
				}
			}
		},
		accessToken
	);

	const cityValue = filterConfig?.city ? `City: ${filterConfig?.city}` : '';

	let tempValue = '';
	if (filterConfig?.min_temperature && filterConfig?.max_temperature) {
		tempValue = `Temperature: ${filterConfig.min_temperature}°C - ${filterConfig.max_temperature}°C`;
	} else if (filterConfig?.min_temperature) {
		tempValue = `Temperature: ${filterConfig.min_temperature}°C+`;
	} else if (filterConfig?.max_temperature) {
		tempValue = `Temperature: Up to ${filterConfig.max_temperature}°C`;
	}

	const timeValue = filterConfig?.time_filter
		? `Duration: ${
				durationFilterOptions.find((opt) => opt.value === filterConfig?.time_filter)?.label
		  }`
		: '';
	const statusValue = filterConfig?.status ? `Status: ${filterConfig?.status}` : '';
	const alertStatusValue = filterConfig?.alert_status
		? `Alert: ${
				alertStatusOptions.find((opt) => opt.value === filterConfig?.alert_status)?.label
		  }`
		: '';

	const cityAllValue = filterConfig?.city === '' ? 'City: All' : '';
	const timeAllValue = filterConfig?.time_filter === '' ? 'Duration: All' : '';
	const statusAllValue = filterConfig?.status === '' ? 'Status: All' : '';
	const batteryStatusAllValue = filterConfig?.battery_status === '' ? 'Battery: All' : '';
	const alertStatusAllValue = filterConfig?.alert_status === '' ? 'Alert: All' : '';

	const paginationArray = [
		cityValue,
		tempValue,
		timeValue,
		statusValue,
		alertStatusValue,
		cityAllValue,
		timeAllValue,
		statusAllValue,
		batteryStatusAllValue,
		alertStatusAllValue,
	];
	const validArray = paginationArray.filter((n) => n);

	const fetchAlerts = useCallback(() => {
		if (alertDataReceived) return;

		setAlertsLoading(true);
		dispatch(getTemperatureListAction({ search_type: 'temperature_alert' }))
			.then((res) => {
				if (res && res.rows) {
					setAlerts(res.rows);
				}
			})
			.catch((error) => {
				console.error('Error fetching alerts:', error);
			})
			.finally(() => {
				setAlertsLoading(false);
			});
	}, [dispatch, alertDataReceived]);

	const fetchTemperatureData = useCallback(() => {
		setIsLoadingData(true);
		dispatch(getTemperatureListAction({ search_type: 'temperature_tracking' }))
			.then((res) => {
				if (res && res.rows) {
					setData(res.rows);
					const uniqueCities = [...new Set(res.rows.map((item) => item.city))]
						.filter(Boolean)
						.map((city) => ({
							value: city,
							label: city,
						}));
					setCities([allOption, ...uniqueCities]);

					const uniqueStatuses = [...new Set(res.rows.map((item) => item.status))]
						.filter(Boolean)
						.map((status) => ({
							value: status,
							label: status,
						}));
					setStatuses([allOption, ...uniqueStatuses]);
				}
			})
			.finally(() => {
				setIsLoadingData(false);
				// After direct API fetch, set temperatureDataReceived to true to avoid duplicate fetches
				setTemperatureDataReceived(true);
			});
	}, [dispatch]);

	useEffect(() => {
		if (!isConnected) {
			fetchAlerts();
		}
	}, [fetchAlerts, isConnected]);

	useEffect(() => {
		if (temperatureDataReceived) return;

		dispatch(getTemperatureListAction({ search_type: 'temperature_tracking' })).then((res) => {
			if (res && res.rows) {
				const uniqueCities = [...new Set(res.rows.map((item) => item.city))]
					.filter(Boolean)
					.map((city) => ({
						value: city,
						label: city,
					}));
				setCities([allOption, ...uniqueCities]);

				const uniqueStatuses = [...new Set(res.rows.map((item) => item.status))]
					.filter(Boolean)
					.map((status) => ({
						value: status,
						label: status,
					}));
				setStatuses([allOption, ...uniqueStatuses]);
			}
		});
	}, [dispatch, temperatureDataReceived]);

	const handleSearch = (value) => {
		const newFilters = {
			...activeFilters,
			query_str: value,
		};
		setActiveFilters(newFilters);

		if (socketConnection && isConnected) {
			socketConnection.emit('temperature_data', newFilters);
		}

		setFilterConfig({
			...filterConfig,
			query_str: value,
		});
	};

	const handleFilterSubmit = (values) => {
		const wsFilters = {};

		if (values.city) wsFilters.city = values.city;
		if (values.min_temperature)
			wsFilters.minimum_temperature = parseFloat(values.min_temperature);
		if (values.max_temperature)
			wsFilters.maximum_temperature = parseFloat(values.max_temperature);
		if (values.time_filter) wsFilters.duration = values.time_filter;
		if (values.status) wsFilters.status = values.status;
		if (values.alert_status) wsFilters.alert_status = values.alert_status;

		setActiveFilters(wsFilters);

		if (socketConnection && isConnected) {
			socketConnection.emit('temperature_data', wsFilters);
			socketConnection.emit('alert_data', wsFilters);
		}

		const updatedConfig = {
			...filterConfig,
			search_type: 'temperature_tracking',
			filter_by: {},
			filter_list: {},
		};

		const filterKeys = [
			'city',
			'min_temperature',
			'max_temperature',
			'time_filter',
			'status',
			'battery_status',
			'alert_status',
		];
		filterKeys.forEach((key) => {
			updatedConfig[key] = undefined;
		});

		Object.keys(values).forEach((key) => {
			if (values[key]) {
				updatedConfig[key] = values[key];
			}
		});

		updatedConfig.filter_by = {};
		Object.keys(values).forEach((key) => {
			if (values[key]) {
				updatedConfig.filter_by[key] = values[key];
			}
		});

		setFilterConfig(updatedConfig);
		setOpen(false);
	};

	const handleReset = () => {
		form.resetFields();

		const resetConfig = {
			...initialPaginateConfig,
			search_type: 'temperature_tracking',
			filter_by: {},
			filter_list: {},
		};

		const filterKeys = [
			'city',
			'min_temperature',
			'max_temperature',
			'time_filter',
			'status',
			'battery_status',
			'alert_status',
		];

		// Set all filter keys to undefined to ensure they're removed from URL
		filterKeys.forEach((key) => {
			resetConfig[key] = undefined;
		});

		setActiveFilters({});

		if (socketConnection && isConnected) {
			socketConnection.emit('temperature_data', {});
			socketConnection.emit('alert_data', {});
			setTemperatureDataReceived(false);
			setAlertDataReceived(false);
		} else {
			if (!socketConnection) {
				initializeSocket();
			}
			setTimeout(() => {
				if (socketConnection?.connected) {
					socketConnection.emit('temperature_data', {});
					socketConnection.emit('alert_data', {});
					setTemperatureDataReceived(false);
					setAlertDataReceived(false);
				} else {
					fetchTemperatureData();
					fetchAlerts();
				}
			}, 100);
		}

		// Clear URL parameters by passing the clean resetConfig to setFilterConfig
		setFilterConfig(resetConfig);
	};

	const handleResetFilters = () => {
		handleReset();
		setOpen(false);
	};

	const handleViewAlert = (alert) => {
		setSelectedAlert(alert);
		setAlertModalVisible(true);
	};

	const handleCloseAlertModal = () => {
		setAlertModalVisible(false);
		setSelectedAlert(null);
	};

	const handleLoadingHistoryClick = (record) => {
		setSelectedTripForHistory(record);
		setLoadingHistoryModalVisible(true);
	};

	const handleCloseLoadingHistoryModal = () => {
		setLoadingHistoryModalVisible(false);
		setSelectedTripForHistory(null);
	};

	// Set up global handler for the table action button
	useEffect(() => {
		window.onLoadingHistoryClick = handleLoadingHistoryClick;
		return () => {
			window.onLoadingHistoryClick = null;
		};
	}, []);

	return (
		<>
			<TableHeaderSection
				handleSearch={handleSearch}
				handleReset={handleReset}
				handleResetFilters={handleResetFilters}
				setOpen={setOpen}
				cityValue={cityValue}
				tempValue={tempValue}
				timeValue={timeValue}
				statusValue={statusValue}
				alertStatusValue={alertStatusValue}
				cityAllValue={cityAllValue}
				timeAllValue={timeAllValue}
				statusAllValue={statusAllValue}
				alertStatusAllValue={alertStatusAllValue}
				validArray={validArray}
				isConnected={isConnected}
			/>
			<Box mt={3}>
				<InfiniteScrollTable
					rowKey="trip_id"
					data={data}
					setData={setData}
					dispatchAction={getTemperatureListAction}
					columns={TemperatureTableConfig}
					rowClassName={(record) => record.is_breached && 'custom-disabled-table-row'}
					isLoading={isLoadingData}
				/>
			</Box>
			<AlertSection
				alerts={alerts}
				alertsLoading={alertsLoading}
				handleViewAlert={handleViewAlert}
			/>
			<FilterSection
				open={open}
				setOpen={setOpen}
				form={form}
				handleFilterSubmit={handleFilterSubmit}
				handleResetFilters={handleResetFilters}
				cities={cities}
				statuses={statuses}
			/>
			<AlertDetail
				selectedAlert={selectedAlert}
				alertModalVisible={alertModalVisible}
				handleCloseAlertModal={handleCloseAlertModal}
			/>
			<LoadingHistoryModal
				visible={loadingHistoryModalVisible}
				onClose={handleCloseLoadingHistoryModal}
				loadStatusHistory={selectedTripForHistory?.load_status_table || []}
			/>
		</>
	);
}
